import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const ArrancarEmpujandoCoche = () => {
  const navigate = useNavigate();

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: '¿Se Puede Arrancar Empujando el Coche? Guía Completa | ArrancaYa BCN',
    description: 'Descubre cuándo es posible arrancar empujando el coche, qué coches lo permiten, riesgos y por qué es mejor llamar a ArrancaYa BCN para arranque profesional en Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/arrancar-empujando-coche'
  });

  useEffect(() => {

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'arrancar empujando, arranque por empuje, coche manual, batería descargada, push start, Barcelona arranque';
    document.head.appendChild(metaKeywords);

    // Schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "¿Se Puede Arrancar Empujando el Coche? Guía Completa",
      "description": "Guía completa sobre arrancar empujando el coche: cuándo es posible, riesgos y alternativas profesionales",
      "image": "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&h=400&fit=crop",
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "publisher": {
        "@type": "Organization",
        "name": "ArrancaYa BCN",
        "logo": "https://arrancaya-bcn.es/images/logo.png"
      },
      "datePublished": "2026-01-14",
      "dateModified": "2026-01-14"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Arrancar empujando coche',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Consejos'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium">Consejos</span>
            <span>•</span>
            <span>14 de enero, 2026</span>
            <span>•</span>
            <span>4 min de lectura</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            ¿Se Puede Arrancar 
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Empujando</span> el Coche?
          </h1>
          
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-100 mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              La verdad sobre el <strong className="text-orange-600">arranque por empuje</strong>: cuándo funciona, cuándo no, 
              y por qué es mejor llamar a un servicio profesional como <strong className="text-gray-900">ArrancaYa BCN</strong>.
            </p>
          </div>
        </header>

        <div className="prose prose-xl max-w-none space-y-12">
          
          {/* Respuesta rápida - SIN IMAGEN */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white shadow-xl">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
              <span className="bg-white/20 rounded-full p-3 mr-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </span>
              Respuesta Rápida
            </h2>
            <p className="text-blue-100 text-xl leading-relaxed mb-0">
              <strong className="text-white">Sí, se puede arrancar empujando</strong>, pero solo en coches <strong className="text-yellow-300">manuales</strong> 
              y bajo condiciones específicas. En Barcelona, con tráfico y pendientes, 
              es arriesgado y poco práctico. <strong className="text-white">ArrancaYa BCN</strong> te ofrece una solución más segura.
            </p>
          </div>

          <h2>¿Cuándo Es Posible Arrancar Empujando?</h2>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="flex items-center text-green-800 font-bold mb-3">
                <CheckCircle className="h-5 w-5 mr-2" />
                SÍ Funciona Con:
              </h3>
              <ul className="text-green-700 space-y-2">
                <li>• Coches con <strong>cambio manual</strong></li>
                <li>• Batería descargada (no completamente muerta)</li>
                <li>• Motor en buen estado</li>
                <li>• Pendiente o ayuda de varias personas</li>
                <li>• Embrague funcionando correctamente</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="flex items-center text-red-800 font-bold mb-3">
                <XCircle className="h-5 w-5 mr-2" />
                NO Funciona Con:
              </h3>
              <ul className="text-red-700 space-y-2">
                <li>• Coches <strong>automáticos</strong></li>
                <li>• Batería completamente muerta</li>
                <li>• Problema en motor de arranque</li>
                <li>• Avería en bomba de combustible</li>
                <li>• Coches híbridos o eléctricos</li>
              </ul>
            </div>
          </div>

          <h2>Cómo Arrancar Empujando (Solo Coches Manuales)</h2>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 font-semibold mb-2">⚠️ Atención: Procedimiento Arriesgado</p>
                <p className="text-yellow-700 mb-0">
                  Este método puede dañar el embrague, transmisión o catalizador. 
                  Solo úsalo en emergencias y mejor llama a ArrancaYa BCN.
                </p>
              </div>
            </div>
          </div>

          <ol className="space-y-4">
            <li><strong>Preparación:</strong>
              <ul>
                <li>Asegúrate que el freno de mano esté desactivado</li>
                <li>Coloca el coche en una pendiente o consigue ayuda de 3-4 personas</li>
                <li>Pon la llave en posición de contacto (sin arrancar)</li>
              </ul>
            </li>
            
            <li><strong>Posición del cambio:</strong>
              <ul>
                <li>Mete la <strong>2ª marcha</strong> (nunca la 1ª, es muy brusca)</li>
                <li>Mantén el pedal del embrague pisado</li>
              </ul>
            </li>
            
            <li><strong>El empuje:</strong>
              <ul>
                <li>Las personas empujan el coche hasta alcanzar 10-15 km/h</li>
                <li>En pendiente, deja que coja velocidad gradualmente</li>
              </ul>
            </li>
            
            <li><strong>Soltar embrague:</strong>
              <ul>
                <li>Cuando tengas velocidad suficiente, suelta el embrague <strong>de golpe</strong></li>
                <li>El motor debería arrancar por la inercia de las ruedas</li>
                <li>Una vez arrancado, vuelve a pisar el embrague inmediatamente</li>
              </ul>
            </li>
          </ol>

          <h2>Riesgos del Arranque por Empuje</h2>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-8">
            <h3 className="text-red-800 font-bold mb-4">Posibles Daños al Vehículo:</h3>
            <ul className="text-red-700 space-y-2">
              <li>• <strong>Embrague:</strong> Desgaste prematuro o rotura</li>
              <li>• <strong>Transmisión:</strong> Daños en engranajes</li>
              <li>• <strong>Catalizador:</strong> Combustible no quemado puede dañarlo</li>
              <li>• <strong>Motor:</strong> Arranque brusco puede causar averías</li>
              <li>• <strong>Correa de distribución:</strong> Posible desincronización</li>
            </ul>
          </div>

          <h2>¿Por Qué No Funciona en Coches Automáticos?</h2>
          <p>Los coches automáticos no se pueden arrancar empujando porque:</p>
          <ul>
            <li>No tienen embrague manual para desconectar/conectar el motor</li>
            <li>La bomba de aceite de la transmisión necesita motor encendido</li>
            <li>El convertidor de par no permite la conexión directa</li>
            <li>Empujar un automático puede dañar gravemente la transmisión</li>
          </ul>

          <h2>Alternativas Más Seguras</h2>

          <h3>1. Cables de Arranque (Pinzas)</h3>
          <p>La opción más común y segura. Lee nuestro <Link to="/blog/arrancar-coche-con-pinzas" className="text-blue-600 underline">tutorial completo sobre cables de arranque</Link>.</p>

          <h3>2. Arrancador Portátil (Power Bank)</h3>
          <p>Pequeños dispositivos que pueden arrancar tu coche sin necesidad de otro vehículo.</p>

          <h3>3. Servicio Profesional</h3>
          <p>La opción más segura y garantizada.</p>

          <h2>Situaciones Específicas en Barcelona</h2>
          <p>En Barcelona, arrancar empujando es especialmente problemático por:</p>
          <ul>
            <li><strong>Tráfico denso:</strong> Difícil encontrar espacio seguro</li>
            <li><strong>Pendientes pronunciadas:</strong> Peligroso para los ayudantes</li>
            <li><strong>Calles estrechas:</strong> Riesgo de accidente</li>
            <li><strong>Normativa municipal:</strong> Puede estar prohibido en ciertas zonas</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 my-8">
            <p className="text-blue-800 mb-0">
              <strong>💡 Consejo ArrancaYa BCN:</strong> En lugar de arriesgar daños costosos 
              o accidentes, nuestro servicio profesional cuesta desde 69€ y llega en menos de 30 minutos.
            </p>
          </div>

          <h2>¿Cuándo Llamar a un Profesional?</h2>
          <ul>
            <li>Si tu coche es <strong>automático</strong></li>
            <li>Si estás en una <strong>zona de mucho tráfico</strong></li>
            <li>Si no tienes suficiente ayuda o pendiente</li>
            <li>Si el coche es <strong>nuevo o caro</strong> (no arriesgues daños)</li>
            <li>Si no estás seguro de la causa del problema</li>
            <li>Si es de <strong>noche o hace mal tiempo</strong></li>
          </ul>

        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg p-8 mt-12">
          <div className="text-center">
            <Zap className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">¿No Quieres Arriesgar Tu Coche?</h2>
            <p className="text-lg mb-6 opacity-90">
              En ArrancaYa BCN arrancamos tu coche de forma <strong>segura y profesional</strong>. 
              Sin riesgos, sin daños, con garantía. Llegamos en 30 minutos a cualquier punto de Barcelona.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-3 font-bold"
              >
                <Zap className="h-5 w-5 mr-2" />
                Arranque Seguro Ahora
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="bg-white text-green-600 border-2 border-white hover:bg-green-50 px-8 py-3"
              >
                <Phone className="h-5 w-5 mr-2" />
                Llamar: 613 076 099
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              ✅ Sin riesgos para tu coche • ⚡ Servicio profesional • 🛡️ Totalmente asegurado
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default ArrancarEmpujandoCoche;